/**
 * Main App Component
 *
 * Also the auth gate: the backend now requires a bearer token on every data
 * endpoint, so nothing below this point can render usefully without a session.
 */

import React, { useCallback, useEffect, useState } from 'react';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import {
  getToken,
  getStoredUser,
  verifySession,
  UNAUTHORIZED_EVENT,
} from './services/authAPI';
import './App.css';

function App() {
  // 'checking' avoids flashing the login form for an already-signed-in user
  // while the stored token is being validated against the server.
  const [status, setStatus] = useState(() => (getToken() ? 'checking' : 'anon'));
  const [user, setUser] = useState(() => getStoredUser());

  useEffect(() => {
    let cancelled = false;

    if (status !== 'checking') return undefined;

    verifySession()
      .then((verified) => {
        if (cancelled) return;
        if (verified) {
          setUser(verified);
          setStatus('authed');
        } else {
          setStatus('anon');
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Network/server error, NOT a rejected token — verifySession only
        // resolves null when the server actually said 401. Signing the user out
        // because the backend happened to be restarting would be wrong, so keep
        // the stored session; if the token really is dead, the first data
        // request will 401 and the interceptor will bring us back to login.
        setStatus('authed');
      });

    return () => {
      cancelled = true;
    };
  }, [status]);

  // Fired by the API interceptors on any 401, and by an explicit logout.
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setStatus('anon');
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  const handleLoginSuccess = useCallback((loggedInUser) => {
    setUser(loggedInUser);
    setStatus('authed');
  }, []);

  if (status === 'checking') {
    return (
      <div className="App app-booting">
        <div className="app-booting-spinner" />
        <p>Loading…</p>
      </div>
    );
  }

  if (status === 'anon') {
    return (
      <div className="App">
        <LoginPage onSuccess={handleLoginSuccess} />
      </div>
    );
  }

  return (
    <div className="App">
      <DashboardPage user={user} />
    </div>
  );
}

export default App;
